// Shares one enquiry's floating formulas with every place the enquiry screen
// shows them (ledger row 132): the sales-side line on each product card, the
// purchase-side line in each Supplier Pricing block, and the exposure banner.
// One list per enquiry, read once per load and again after every save, so the
// three can never disagree about which formula a line has.

import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import floatingFormulaService from '../../../services/floatingFormulaService';
import { productOneIdOf } from './floatingFormulaGate';
import { serverSentence } from './floatingFormulaText';

const FloatingFormulaContext = createContext(null);

const UNSAVED = { status: 'unsaved', data: null, error: null, version: 0, forId: null };

/**
 * Loads ``GET enquiries/<id>/floating-formulas/``.
 *
 * ``reloadToken`` is the enquiry the form last received: every save hands the
 * form a new one, so a change of token means "read again". While a reload is
 * in flight the previous list stays on screen (product rows keep their ids
 * across saves), so a save never flashes the lines to "Loading" -- but only
 * for the SAME enquiry: opening another one starts from nothing, so its lines
 * are never matched against the last enquiry's formulas.
 *
 * ``status``: 'unsaved' (no enquiry id yet) | 'loading' | 'ready' | 'failed'.
 * ``version`` goes up on every successful read; the exposure banner re-reads
 * the hedging task when it does.
 */
export const useFloatingFormulas = ({ enquiryId, reloadToken }) => {
  const [state, setState] = useState(() => (
    enquiryId ? { ...UNSAVED, status: 'loading', forId: enquiryId } : UNSAVED
  ));
  // Only the newest request may write: a save fires reads close together and
  // an older answer must not land on top of a newer one.
  const latestRequest = useRef(0);

  const reload = useCallback(async () => {
    const request = latestRequest.current + 1;
    latestRequest.current = request;
    if (!enquiryId) {
      setState((prev) => ({ ...UNSAVED, version: prev.version }));
      return;
    }
    setState((prev) => (
      prev.data && prev.forId === enquiryId
        ? prev
        : { ...UNSAVED, status: 'loading', version: prev.version, forId: enquiryId }
    ));
    try {
      const data = await floatingFormulaService.list(enquiryId);
      if (request !== latestRequest.current) return;
      setState((prev) => ({
        status: 'ready', data, error: null, version: prev.version + 1, forId: enquiryId,
      }));
    } catch (err) {
      if (request !== latestRequest.current) return;
      setState((prev) => ({
        ...prev,
        status: 'failed',
        error: serverSentence(err, 'The floating formulas could not be loaded.'),
      }));
    }
  }, [enquiryId]);

  useEffect(() => {
    reload();
  }, [reload, reloadToken]);

  return { ...state, reload };
};

export const FloatingFormulaProvider = ({ formulas, enquiry, isCustomerTrader, children }) => (
  <FloatingFormulaContext.Provider
    value={{
      ...formulas,
      enquiryId: enquiry?.id ?? null,
      displayId: enquiry?.display_id || '',
      productOneId: productOneIdOf(enquiry),
      isCustomerTrader: Boolean(isCustomerTrader),
    }}
  >
    {children}
  </FloatingFormulaContext.Provider>
);

// Null outside the enquiry screen. The formula line and the banner render
// nothing there, which is what the sections' own tests rely on.
export const useFloatingFormulaContext = () => useContext(FloatingFormulaContext);
